import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X, ChevronLeft, ChevronRight, LogOut, Settings2, UserPlus, Ruler } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../data/content";
import { listForGender, genderLabel } from "../data/measurements";
import { Reveal, Tag, EASE } from "./Shared";

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const DuoImg = ({ base, alt, className = "", testId }) => (
  <span className={`contents ${className}`}>
    <img src={base.replace(".webp", "-d.webp")} alt={alt} loading="lazy" data-testid={testId} className="logo-for-dark max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
    <img src={base.replace(".webp", "-l.webp")} alt="" loading="lazy" aria-hidden="true" className="logo-for-light max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
  </span>
);

const HOW_TO = [
  "Stand straight and relaxed — don't hold your breath or suck in.",
  "Keep the measuring tape level and parallel to the floor.",
  "Snug, not tight — slide one finger under the tape.",
];

const DetailModal = ({ item, value, unit, onClose, onSave, onPrev, onNext }) => {
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [item, value]);

  const save = (advance) => {
    if (!val.trim()) return;
    onSave(item.key, val.trim());
    if (advance) onNext();
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 z-[95] bg-ink/80 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="measure-detail-backdrop"
      />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
        <motion.div
          className="grid max-h-full w-full max-w-4xl overflow-y-auto border border-line/10 bg-coal md:grid-cols-2"
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 24 }}
          transition={{ duration: 0.4, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
          data-testid="measure-detail-modal"
        >
        <div className="flex items-center justify-center border-b border-line/10 bg-ink p-6 md:border-b-0 md:border-r">
          <DuoImg base={item.img} alt={`How to measure — ${item.label}`} testId="measure-detail-illustration" className="[&>img]:max-h-[38vh] md:[&>img]:max-h-[64vh]" />
        </div>
        <div className="relative flex min-w-0 flex-col p-7 sm:p-9">
          <button type="button" onClick={onClose} data-testid="measure-detail-close" aria-label="Close" className="absolute right-4 top-4 border border-line/15 p-2 text-fog transition-colors hover:border-flame hover:text-flame">
            <X className="h-4 w-4" />
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-flame">{item.group}</p>
          <h3 className="mt-2 font-serif text-4xl font-light" data-testid="measure-detail-title">{item.label}</h3>

          <div className="mt-5 border border-line/10 bg-ink/40 p-5" data-testid="measure-help-panel">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-flame">
              <Ruler className="h-3.5 w-3.5" /> How to measure
            </p>
            <p className="mt-3 text-sm font-light leading-relaxed text-bone/85">{item.hint}</p>
            <ol className="mt-3 space-y-1.5">
              {HOW_TO.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-xs font-light leading-relaxed text-fog">
                  <span className="font-mono text-flame">{i + 1}.</span> {s}
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-6">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Your measurement ({unit})</p>
            <div className="flex items-end gap-3">
              <input
                inputMode="decimal"
                data-testid="measure-detail-input"
                value={val}
                onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && save(true)}
                placeholder="0"
                className="w-40 border-b-2 border-line/20 bg-transparent pb-2 font-serif text-6xl font-light text-bone placeholder:text-fog/40 focus:border-flame focus:outline-none transition-colors"
              />
              <span className="pb-3 font-mono text-sm uppercase tracking-[0.3em] text-flame">{unit}</span>
            </div>
          </div>

          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-8">
            <button type="button" onClick={onPrev} data-testid="measure-detail-prev" aria-label="Previous measurement" className="border border-line/15 p-3 text-fog transition-colors hover:border-flame hover:text-flame">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={() => save(false)}
                data-testid="measure-detail-save"
                className="whitespace-nowrap border border-flame px-4 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink sm:px-5 sm:tracking-[0.25em]"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => save(true)}
                data-testid="measure-detail-save-next"
                className="whitespace-nowrap bg-flame px-4 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-opacity hover:opacity-90 sm:px-5 sm:tracking-[0.25em]"
              >
                Save & Next →
              </button>
            </div>
            <button type="button" onClick={onNext} data-testid="measure-detail-next" aria-label="Next measurement" className="border border-line/15 p-3 text-fog transition-colors hover:border-flame hover:text-flame">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        </motion.div>
      </div>
    </>
  );
};

const AddMemberModal = ({ onClose, onCreate }) => {
  const [label, setLabel] = useState("");
  const [gender, setGender] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const create = async () => {
    if (!label.trim() || !gender) return;
    setBusy(true);
    setError("");
    try {
      await onCreate(label.trim(), gender);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.div className="fixed inset-0 z-[95] bg-ink/80 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div
        className="fixed left-1/2 top-1/2 z-[100] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 border border-line/10 bg-coal p-8"
        initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
        animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
        exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
        transition={{ duration: 0.35, ease: EASE }}
        data-testid="add-member-modal"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-flame">Family Profile</p>
        <h3 className="mt-2 font-serif text-3xl font-light">Add a member.</h3>
        <p className="mt-2 text-sm font-light text-fog">A separate measurement file — for your child, partner or parent.</p>
        <input
          data-testid="add-member-name-input"
          className="mt-6 w-full border border-line/15 bg-bone/5 px-4 py-3.5 text-sm text-bone placeholder:text-fog/60 focus:border-flame focus:outline-none transition-colors"
          placeholder="Name — e.g. Aarav, Mom, Aai"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[["women", "Women"], ["men", "Men"], ["kids", "Kids"]].map(([g, l]) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(g)}
              data-testid={`add-member-gender-${g}`}
              className={`border px-2 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                gender === g ? "border-flame bg-flame text-ink" : "border-line/15 text-fog hover:border-flame/60 hover:text-bone"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        {error && <p className="mt-3 font-mono text-xs text-flame" data-testid="add-member-error">{error}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="add-member-cancel" className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-flame">Cancel</button>
          <button
            type="button"
            onClick={create}
            disabled={busy || !label.trim() || !gender}
            data-testid="add-member-create-button"
            className="bg-flame px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {busy ? "Creating..." : "Create Profile"}
          </button>
        </div>
      </motion.div>
    </>
  );
};

const Tailor = ({ user, onLogout, onEditProfile }) => {
  const unit = user.profile.units === "cm" ? "cm" : "in";
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [values, setValues] = useState({});
  const [activeIdx, setActiveIdx] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/measure/profiles").then(({ data }) => {
      setProfiles(data.profiles);
      setActiveId(data.profiles[0]?.id || null);
    }).catch(() => {});
  }, []);

  const active = profiles.find((p) => p.id === activeId);
  const list = useMemo(
    () => listForGender(active?.gender || user.profile.gender).map((it) => ({ ...it, variant: active?.gender || user.profile.gender })),
    [active, user.profile.gender]
  );

  useEffect(() => {
    if (active) setValues(active.values || {});
  }, [activeId, profiles]);

  const done = useMemo(() => list.filter((it) => values[it.key]).length, [list, values]);
  const pct = list.length ? Math.round((done / list.length) * 100) : 0;

  const save = async (key, value) => {
    const next = { ...values, [key]: value };
    setValues(next);
    setProfiles((ps) => ps.map((p) => (p.id === activeId ? { ...p, values: next } : p)));
    try {
      await api.put(`/measure/profiles/${activeId}/measurements`, { values: next });
      toast.success(`${list.find((l) => l.key === key)?.label} saved — ${value} ${unit}`);
    } catch {
      toast.error("Could not save. Please try again.");
    }
  };

  const createProfile = async (label, gender) => {
    const { data } = await api.post("/measure/profiles", { label, gender });
    setProfiles((ps) => [...ps, data]);
    setActiveId(data.id);
    toast.success(`${label}'s measurement file created`);
  };

  const groups = ["Upper Body", "Lower Body"];

  return (
    <div data-testid="tailor-overview">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <Tag>The Digital Tailor</Tag>
          <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl">
            THE ATELIER <span className="text-flame">FILE.</span>
          </h1>
          <p className="mt-4 max-w-lg text-base font-light text-fog">
            {user.name} — {user.profile.fit} fit{user.profile.height ? ` · Height ${user.profile.height} ${unit}` : ""} · {profiles.length} profile{profiles.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onEditProfile} data-testid="tailor-edit-profile-button" className="flex items-center gap-2 border border-line/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-flame hover:text-flame">
            <Settings2 className="h-3.5 w-3.5" /> Profile
          </button>
          <button type="button" onClick={onLogout} data-testid="tailor-logout-button" className="flex items-center gap-2 border border-line/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-flame hover:text-flame">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="profile-switcher">
        {profiles.map((p) => {
          const plist = listForGender(p.gender);
          const pdone = plist.filter((it) => (p.values || {})[it.key]).length;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => { setActiveId(p.id); setActiveIdx(null); }}
              data-testid={`profile-chip-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-2.5 border px-3.5 py-2.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-all duration-300 sm:px-5 sm:py-3 sm:text-[10px] sm:tracking-[0.2em] ${
                p.id === activeId ? "border-flame bg-flame/10 text-flame" : "border-line/15 text-fog hover:border-flame/50 hover:text-bone"
              }`}
            >
              {p.label}
              <span className="text-[9px] opacity-70">{genderLabel(p.gender)} · {pdone}/{plist.length}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="add-member-button"
          className="flex items-center gap-2 border border-dashed border-flame/50 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink"
        >
          <UserPlus className="h-3.5 w-3.5" /> Add Member
        </button>
      </div>

      <div className="mt-8 border border-line/10 bg-coal p-6 sm:p-8" data-testid="tailor-progress">
        <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-fog sm:text-[10px] sm:tracking-[0.3em]">
          <span>{active?.label} — {genderLabel(active?.gender || "women")} measurement progress</span>
          <span className="text-bone">{done} of {list.length} · <span className="text-flame">{pct}%</span></span>
        </div>
        <div className="mt-4 h-1 w-full bg-line/10">
          <motion.div className="h-full bg-flame" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: EASE }} />
        </div>
        {done === list.length && list.length > 0 && (
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-flame" data-testid="tailor-complete-note">
            Complete — the atelier has everything it needs for {active?.label}. Book a consultation when you're ready.
          </p>
        )}
      </div>

      {groups.map((g) => (
        <div key={g} className="mt-16">
          <Reveal>
            <h2 className="font-serif text-3xl font-light tracking-wide text-bone/90">{g}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {list.filter((it) => it.group === g).map((it) => {
              const filled = Boolean(values[it.key]);
              const idx = list.findIndex((l) => l.key === it.key);
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  data-testid={`measurement-card-${it.key}`}
                  className={`group relative border p-5 text-left transition-all duration-500 hover:-translate-y-1 ${
                    filled
                      ? "border-flame bg-flame/10"
                      : "border-line/10 bg-coal hover:border-flame/40"
                  }`}
                >
                  {filled && <span className="absolute left-0 top-0 h-full w-1 bg-flame" />}
                  <div className="flex h-36 items-center justify-center overflow-hidden sm:h-44">
                    <DuoImg base={it.img} alt={`${it.label} measurement guide`} testId={`measurement-illustration-${it.key}`} />
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div>
                      <p className="font-serif text-lg font-normal leading-tight">{it.label}</p>
                      <p className={`mt-1 font-mono text-[10px] uppercase tracking-[0.2em] ${filled ? "text-flame" : "text-fog"}`} data-testid={`measurement-value-${it.key}`}>
                        {filled ? `${values[it.key]} ${unit} — done` : "Tap to add"}
                      </p>
                    </div>
                    {filled && (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-flame" data-testid={`measurement-done-${it.key}`}>
                        <Check className="h-3.5 w-3.5 text-ink" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <AnimatePresence>
        {activeIdx !== null && (
          <DetailModal
            item={list[activeIdx]}
            value={values[list[activeIdx].key]}
            unit={unit}
            onClose={() => setActiveIdx(null)}
            onSave={save}
            onPrev={() => setActiveIdx((i) => (i - 1 + list.length) % list.length)}
            onNext={() => setActiveIdx((i) => (i + 1) % list.length)}
          />
        )}
        {adding && <AddMemberModal onClose={() => setAdding(false)} onCreate={createProfile} />}
      </AnimatePresence>
    </div>
  );
};

export default Tailor;
